import { bps } from './money'
import type { Minor, NetworkConfig, Settlement } from './types'

/**
 * Settlement is the only point at which money genuinely moves, and it moves
 * between banks rather than between a cardholder and a merchant.
 *
 * The chain runs issuer to Visa to acquirer to merchant, and every link takes
 * something out. The merchant never receives what the cardholder paid, which is
 * the single fact most people are surprised by.
 *
 * The rates are illustrative. South African interchange is regulated and
 * published by the SARB, and real rates vary by card type, channel and merchant
 * category. See docs/sources.md.
 */
export function computeSettlement(
  id: string,
  transactionId: string,
  clearingId: string,
  day: number,
  grossMinor: Minor,
  fees: NetworkConfig['fees'],
): Settlement {
  const interchangeMinor = bps(grossMinor, fees.interchangeBps)
  const schemeFeeMinor = bps(grossMinor, fees.schemeFeeBps)
  const merchantServiceCharge = bps(grossMinor, fees.merchantServiceChargeBps)

  // Whatever the acquirer charges the merchant, less what it has to pass on.
  const acquirerMarginMinor = merchantServiceCharge - interchangeMinor - schemeFeeMinor

  return {
    id,
    transactionId,
    clearingId,
    day,
    grossMinor,
    interchangeMinor,
    schemeFeeMinor,
    acquirerMarginMinor,
    // The issuer keeps the interchange reimbursement fee, so it pays Visa less than it took
    // from the cardholder.
    issuerPaysMinor: grossMinor - interchangeMinor,
    // Visa takes its own fees before paying the acquirer.
    schemePaysMinor: grossMinor - interchangeMinor - schemeFeeMinor,
    // The merchant is charged one blended rate that covers all of the above.
    merchantNetMinor: grossMinor - merchantServiceCharge,
  }
}
